import { h } from 'preact';
import Store from 'store/store';
import * as jsonPointer from 'utils/jsonPointer';
import { canUser } from 'utils/roles';
import { joinClasses, sortObjectByOrder } from 'utils/utils';

import Facebook from 'components/bolt/icons/facebook';
import Twitter from 'components/bolt/icons/twitter';
import Instagram from 'components/bolt/icons/instagram';
import Youtube from 'components/bolt/icons/youtube';
import Tiktok from 'components/bolt/icons/tiktok';

import MovieEditor from 'components/editor/movies/movieEditor';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import Toggle from 'components/toggle/toggle';

import visualStyles from 'components/bolt/socials/socials.sss';
import settingsStyles from 'components/editor/movies/home/home.sss';

const icons = {
	facebook: Facebook,
	twitter: Twitter,
	instagram: Instagram,
	youtube: Youtube,
	tiktok: Tiktok,
};

export default class SocialsEditor extends MovieEditor {

	constructor(props) {
		super(props);

		this.onToggle = this.onToggle.bind(this);
	}

	onToggle(path) {
		const value = jsonPointer.get(Store.get().data, path);
		jsonPointer.setDeep(Store.get().data, path, !value);
	}

	getIcon(id, data) {
		const { iconStyle } = this.props;
		const { iconUrl } = data || {};
		
		const Icon = icons[id];
		const attr = { class: iconStyle || visualStyles.icon };

		if (Icon) {
			return <Icon {...attr} />;
		}

		if (iconUrl) {
			return <img {...attr} src={iconUrl} alt="" />;
		}

		return null;
	}

	renderVisual() {
		const { iconStyle, class: className, ...props } = this.props;

		const socials = sortObjectByOrder(
			this.movie?.pages?.home?.socials,
			(v, id) => v?.enabled === false ? null : [id, v]
		).filter(Boolean);

		return (
			<SelectableSection {...props} class={joinClasses(visualStyles.socials, className)} section="/pages/home/socials">
				{socials.map(([id, social]) => (
					<div
						class={joinClasses(!iconStyle && visualStyles.social, visualStyles[id])}
						key={id}
					>
						{this.getIcon(id, social)}
					</div>
				))}
			</SelectableSection>
		);
	}

	renderSocialSettings(socialIndex) {
		const dataRoot = this.props.dataRoot;
		const socials = jsonPointer.get(Store.get().data, `${dataRoot}/content/pages/home/socials`) || {};
		const social = socials[socialIndex] || { enabled: false, destination: '' };
		const readonly = !canUser('write');

		return (
			<div class={settingsStyles.itemEditor}>
				<SimpleField
					dark
					path={`${dataRoot}/content/pages/home/socials/${socialIndex}/destination`}
					label={`${socialIndex.charAt(0).toUpperCase() + socialIndex.slice(1)} link`}
					placeholder="https://"
					disabled={readonly || !social.enabled}
				/>
			</div>
		);
	}

	renderSocialsSettings() {
		const socialsPath = `${this.props.dataRoot}/content/pages/home/socials`;
		const socialsData = jsonPointer.get(Store.get().data, socialsPath) || {};
		const socialsList = ['facebook', 'instagram', 'tiktok', 'twitter', 'youtube'];

		return (
			<div class={settingsStyles.itemGroup}>
				{socialsList.map((key) => {
					const social = socialsData[key] || { enabled: false, destination: '' };
					return (
						<Toggle checked={social.enabled} onInput={() => this.onToggle(`${socialsPath}/${key}/enabled`)} class={settingsStyles.toggle} key={key}>
							<div class={joinClasses(settingsStyles.item)}>
								<span>{key.charAt(0).toUpperCase() + key.slice(1)}</span>
								<button class={joinClasses(settingsStyles.edit, !social.enabled && settingsStyles.disabled)} disabled={!social.enabled} onClick={() => this.props.select(`/pages/home/socials/${key}`)}>
									Edit
								</button>
							</div>
						</Toggle>
					);
				})}
			</div>
		);
	}

	renderSettings() {
		const activeElement = this.props.activeElement;

		const nav = [
			{ label: 'Home', path: '/pages/home' },
			{ label: 'Social media', path: `/pages/home/socials` }
		];
		let content = this.renderSocialsSettings();
		
		// if activeElement has another / after socials, go to that social setting
		const activeElementParts = activeElement.split('/');
		if (activeElementParts.length > 4) {
			const socialIndex = activeElementParts.pop();
			const socialLabel = socialIndex.charAt(0).toUpperCase() + socialIndex.slice(1);

			nav.push({ label: socialLabel, path: activeElement });
			content = this.renderSocialSettings(socialIndex);
		}

		return (
			<div class={settingsStyles.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}
